import React from 'react';
import * as styles from './unitsModule.styles';

type Unit = {
  modelId: string;
  name: string;
  availabilityDate: string;
  beds: number;
  baths: number;
  sqft: number;
  low: number;
  high: number;
  priceRangeDisplay: Array<string>;
  isNewUnit?: boolean;
  createdAgo?: string;
  priceDrop?: number;
};

type Floorplan = {
  modelId: string;
  name: string;
  beds: number;
  baths: number;
  sqft: number;
  low: number;
  high: number;
  priceRangeDisplay: Array<string>;
  photos: Array<{ url: string }>;
  largePhotos: Array<{ url: string }>;
  layoutCount?: number;
  units: Array<Unit>;
};

type UnitTableProps = {
  units: Array<Unit>;
  floorplan: Floorplan;
  onUnitSelect: (unit: Unit, floorplan: Floorplan) => void;
  maxRows?: number;
  onSeeAll?: () => void;
};

const formatRent = (low: number): string => {
  if (!low || low === 0) return 'Contact';
  return `$${low.toLocaleString('en-US')}`;
};

const formatAvailability = (date: string): string => {
  if (!date) return 'Now';
  const lower = date.toLowerCase();
  if (lower === 'now' || lower === 'available now' || lower === 'available') return 'Now';

  const parsed = new Date(date);
  if (!isNaN(parsed.getTime())) {
    const year = parsed.getUTCFullYear();
    const month = parsed.getUTCMonth() + 1;
    const day = parsed.getUTCDate();
    return `${month}/${day}/${year}`;
  }

  return date;
};

export const UnitTable = ({ units, floorplan, onUnitSelect, maxRows, onSeeAll }: UnitTableProps) => {
  const hasUnits = units && units.length > 0;
  const displayedUnits = maxRows && hasUnits ? units.slice(0, maxRows) : units;
  const hasMore = maxRows && hasUnits && units.length > maxRows;
  const totalCount = hasUnits ? units.length : 0;

  return (
    <>
      <table className={styles.unitTable}>
        <colgroup>
          <col style={{ width: '27%' }} />
          <col style={{ width: '33%' }} />
          <col style={{ width: 'auto' }} />
          <col style={{ width: '28px' }} />
        </colgroup>
        <thead>
          <tr>
            <th className={styles.unitTableHeader}>Unit</th>
            <th className={styles.unitTableHeader}>Rent/mo</th>
            <th className={styles.unitTableHeaderAvailable}>Available</th>
            <th className={styles.unitTableHeaderChevron} aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {hasUnits ? (
            displayedUnits.map((unit) => (
              <tr
                key={unit.modelId || unit.name}
                className={styles.unitTableRow}
                onClick={() => onUnitSelect(unit, floorplan)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onUnitSelect(unit, floorplan);
                  }
                }}
              >
                <td className={styles.unitNameCell}>
                  <span className={styles.unitNameCellContent}>
                    <span className={styles.badgeArea}>
                      {unit.isNewUnit && <span className={styles.unitNewBadge}>New</span>}
                    </span>
                    <span className={styles.unitNameText}>{unit.name}</span>
                  </span>
                </td>
                <td className={styles.cellWithBadgeArea}>
                  <span className={styles.rentCellContent}>
                    <span className={styles.badgeArea}>
                      {unit.priceDrop ? (
                        <span className={styles.priceDropOriginal}>{formatRent(unit.low + unit.priceDrop)}</span>
                      ) : null}
                    </span>
                    <span className={styles.rentValue}>
                      <span className={styles.rentText}>{formatRent(unit.low)}</span>
                      {unit.priceDrop ? (
                        <span
                          className={styles.priceDropArrow}
                          aria-label={`$${unit.priceDrop.toLocaleString('en-US')} price drop`}
                        >
                          {'\u2193'}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </td>
                <td className={styles.availableCell}>{formatAvailability(unit.availabilityDate)}</td>
                <td className={styles.chevronCell} aria-hidden="true">
                  <span className={styles.chevronChar}>{'\u203A'}</span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td className={styles.unitNameCell}>
                <span className={styles.unitNameCellContent}>
                  <span className={styles.badgeArea} />
                  <span className={styles.unitNameText}>{'\u2014'}</span>
                </span>
              </td>
              <td className={styles.cellWithBadgeArea}>
                <span className={styles.rentCellContent}>
                  <span className={styles.badgeArea} />
                  <span className={styles.rentValue}>
                    <span className={styles.rentText}>{'\u2014'}</span>
                  </span>
                </span>
              </td>
              <td className={styles.availableCell}>{'\u2014'}</td>
              <td className={styles.chevronCell} />
            </tr>
          )}
        </tbody>
      </table>

      {hasMore && onSeeAll && (
        <button type="button" className={styles.seeAllLink} onClick={onSeeAll}>
          See all {totalCount} units <span className={styles.seeAllArrow}>{'\u2192'}</span>
        </button>
      )}
    </>
  );
};

export type { Unit, Floorplan };
